// qs-BackgroundColorControl.vue
<template>
  <div class="background-color-control">
    <div
      class="background-control"
      @click="toggleColorPicker"
      @mouseleave="handleMouseLeave"
    >
      <div
        class="color-preview clickable"
        :style="{ backgroundColor: backgroundColor }"
        :title="'Change background color'"
      />
      <div class="background-label">
        Background
      </div>
    </div>
    <div
      v-if="showColorPicker"
      class="background-color-picker show"
    >
      <ColorPicker
        :key="'background-color-picker'"
        :model-value="backgroundColor"
        :show-opacity="false"
        :show-apply-button="true"
        :show-preview="false"
        :isolate-mode="true"
        @update:model-value="handleColorChange"
        @apply="handleColorApply"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import ColorPicker from '@/components/ui/qs-ColorPicker.vue'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useToolStore } from '@/stores/qs-store-tools'
import { cursorManager } from '@/utils/qs-cursorManager'

const emit = defineEmits<{
  'background-color-changed': [color: string]
}>()

const toolStore = useToolStore()
const canvasStore = useCanvasStore()

const showColorPicker = ref(false)
const previewColor = ref<string | null>(null)

const backgroundColor = computed(() => toolStore.backgroundColor)

const toggleColorPicker = () => {
  showColorPicker.value = !showColorPicker.value
}

const handleColorChange = (newColor: string) => {
  previewColor.value = newColor
  window.dispatchEvent(new CustomEvent('background-color-preview', {
    detail: { color: newColor }
  }))
}

const handleColorApply = () => {
  if (previewColor.value) {
    toolStore.setBackgroundColor(previewColor.value)
  }
  previewColor.value = null
  showColorPicker.value = false
  window.dispatchEvent(new CustomEvent('background-color-preview-clear'))
  emit('background-color-changed', toolStore.backgroundColor)
  const activeElement = document.activeElement as HTMLElement
  if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'BUTTON')) {
    activeElement.blur()
  }
  canvasStore.setPanelHovered(false)
  nextTick(() => {
    const canvasContainer = document.querySelector('.canvas-container') as HTMLElement
    if (canvasContainer) {
      canvasContainer.focus()
    }
    window.dispatchEvent(new CustomEvent('tool-state-refresh', {
      detail: {
        tool: toolStore.currentTool,
        description: 'Background color applied - refreshing tool state'
      }
    }))
    window.dispatchEvent(new CustomEvent('panel-hover', {
      detail: { isHovered: false }
    }))
  })
}

const handleMouseLeave = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target && (target.classList.contains('background-control') ||
                 target.classList.contains('background-color-field'))) {
    target.blur()
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (cursorManager.isAnyInputActive()) {
    return
  }
  if (event.key === 'Escape' && showColorPicker.value) {
    previewColor.value = null
    showColorPicker.value = false
    window.dispatchEvent(new CustomEvent('background-color-preview-clear'))
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped>
.background-color-control {
  width: auto;
}

.background-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: 0;
  margin-bottom: 0;
  transition: all 0.2s ease;
}

.background-control:hover {
  background: theme('colors.creme.hoverpanel');
}

.color-preview {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: theme('borderRadius.DEFAULT');
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.color-preview.clickable {
  cursor: pointer;
}

.background-control:hover .color-preview.clickable {
  border: 1px solid theme('colors.creme.hoverbutton');
  box-sizing: border-box;
}

.background-label {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: theme('colors.text.DEFAULT');
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.background-color-picker {
  width: 100%;
  background: transparent;
  border: none;
  transform: translateY(-100%);
  transition: transform 0.3s ease;
  position: relative;
  z-index: 10;
}

.background-color-picker.show {
  transform: translateY(0);
}
</style>